/**
 * `<pg-card>` — contêiner visual básico, usado como base de qualquer
 * bloco de conteúdo (widget de dashboard, item de lista, painel).
 *
 * Slots:
 * - `header` (opcional) — cabeçalho do card
 * - `header-acao` (opcional) — ação alinhada à direita do cabeçalho
 *   (ex: link "Ver todos →"); só ocupa espaço quando `header` também
 *   está presente (evita uma ação "flutuando" sem título ao lado).
 * - padrão (sem nome) — corpo do card
 * - `footer` (opcional) — rodapé do card
 *
 * Atributo `padding`: 'compacto' | 'normal' (padrão) | 'confortavel'
 *
 * Uso:
 * ```html
 * <pg-card>
 *   <span slot="header">Receita hoje</span>
 *   <a slot="header-acao" href="#/financeiro">Ver financeiro →</a>
 *   <strong>R$ 4.320,00</strong>
 * </pg-card>
 * ```
 */
export class PgCard extends HTMLElement {
    static get observedAttributes() {
        return ['padding'];
    }
    root;
    constructor() {
        super();
        this.root = this.attachShadow({ mode: 'open' });
    }
    connectedCallback() {
        this.render();
    }
    attributeChangedCallback() {
        this.render();
    }
    render() {
        const padding = this.getAttribute('padding') ?? 'normal';
        const espacamento = padding === 'compacto'
            ? 'var(--pg-space-3, 12px)'
            : padding === 'confortavel'
                ? 'var(--pg-space-6, 32px)'
                : 'var(--pg-space-5, 24px)';
        this.root.innerHTML = `
      <style>
        :host {
          display: block;
          height: 100%;
        }
        .card {
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-lg, 16px);
          box-shadow: var(--pg-shadow-sm, 0 1px 2px rgba(0,0,0,0.4));
          color: var(--pg-color-texto-primario, #f2f3f5);
          font-family: var(--pg-font-familia-base, system-ui);
          overflow: hidden;
          height: 100%;
          display: flex;
          flex-direction: column;
        }
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: var(--pg-space-3, 12px);
          padding: ${espacamento} ${espacamento} 0;
          font-size: var(--pg-font-size-sm, 0.875rem);
          color: var(--pg-color-texto-secundario, #9aa1ab);
          font-weight: var(--pg-font-weight-medio, 500);
        }
        .header:not(:has(::slotted(*))) { display: none; }
        .header-acao { flex-shrink: 0; }
        .body {
          padding: ${espacamento};
          flex: 1;
          min-height: 0;
        }
        .footer {
          padding: 0 ${espacamento} ${espacamento};
          border-top: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          margin-top: var(--pg-space-3, 12px);
          padding-top: var(--pg-space-3, 12px);
        }
        .footer:empty { display: none; margin: 0; border: none; padding: 0; }
      </style>
      <div class="card">
        <div class="header">
          <span class="header-titulo"><slot name="header"></slot></span>
          <span class="header-acao"><slot name="header-acao"></slot></span>
        </div>
        <div class="body"><slot></slot></div>
        <div class="footer"><slot name="footer"></slot></div>
      </div>
    `;
    }
}
customElements.define('pg-card', PgCard);
//# sourceMappingURL=pg-card.js.map